/**
 * Wave R5 polish — light specialised dashboards for ops staff that
 * previously shared the generic StaffDashboard.
 *
 * Each dashboard is intentionally focused on the staff member's job:
 *   * TransportStaffDashboard — pickup roster + route notes
 *   * InventoryStaffDashboard — stock / supplies overview
 *   * SupportStaffDashboard   — incoming requests + escalations
 *   * VendorDashboard         — own supplies + documents
 *
 * They re-use Panel/StatCard from Bits.jsx so they inherit the brand.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import {
  Bus, Truck, Package, ChatCircleText, Storefront, Heart,
  ShieldCheck, Path, ForkKnife, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";


// ───────────────────────── Transport Staff ─────────────────────────


export function TransportStaffDashboard() {
  const { user } = useAuth();
  const [pickups, setPickups] = useState([]);
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.get("/documents/stats/summary").catch(() => ({ data: null }));
        setDocs(d.data);
      } catch (e) {
        // best-effort, no toast
      }
    })();
  }, []);

  return (
    <div data-testid="transport-staff-dashboard">
      <PageHeader
        eyebrow="Transport"
        title={`Good morning, ${user?.name?.split(" ")[0] || "Driver"} 🚌`}
        subtitle="Today's pickup roster, route notes, and child safety checks."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Pickups today" value={pickups.length} icon={Bus} accent="terracotta" />
        <StatCard label="Active routes" value={new Set(pickups.map(p => p.route_id)).size || 0} icon={Path} accent="mustard" />
        <StatCard label="Late pickups (>5 min)" value={0} icon={Clock} accent="lavender" />
        <StatCard label="Documents pending" value={docs?.by_status?.pending_review || 0} icon={ShieldCheck} accent="sage" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Today's pickup list">
          <div className="py-12 text-center text-ink-soft text-sm">
            No pickups assigned to you yet today.
          </div>
        </Panel>
        <Panel title="Quick actions">
          <Link to="/app/documents" className="block p-3 rounded-xl bg-terracotta-soft hover:bg-terracotta-soft/80 transition">
            <div className="flex items-center gap-2 text-terracotta font-semibold text-sm"><ShieldCheck size={16} /> My documents</div>
            <div className="text-xs text-ink-soft mt-1">Upload licence, vehicle papers, fitness certificate.</div>
          </Link>
          <Link to="/app/operations" className="block p-3 mt-2 rounded-xl bg-sage-soft hover:bg-sage-soft/80 transition">
            <div className="flex items-center gap-2 text-sage-deep font-semibold text-sm"><Bus size={16} /> Pickup log</div>
            <div className="text-xs text-ink-soft mt-1">Log on-route and drop-off times.</div>
          </Link>
        </Panel>
      </div>
    </div>
  );
}


// ───────────────────────── Inventory Staff ─────────────────────────


export function InventoryStaffDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ uniforms: 0, books: 0, kits: 0, low: 0 });

  useEffect(() => {
    // No inventory stats endpoint yet — show placeholder values gracefully.
    // (Once /api/inventory/stats lands the Wave-R5 polish can pull real data.)
  }, []);

  return (
    <div data-testid="inventory-staff-dashboard">
      <PageHeader
        eyebrow="Inventory"
        title={`Hello, ${user?.name?.split(" ")[0] || "Keeper"} 📦`}
        subtitle="Stock at a glance — uniforms, books, learning kits, and low-stock alerts."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Uniforms in stock" value={stats.uniforms || 0} icon={Package} accent="terracotta" />
        <StatCard label="Books on shelf" value={stats.books || 0} icon={Package} accent="mustard" />
        <StatCard label="Learning kits" value={stats.kits || 0} icon={Package} accent="sage" />
        <StatCard label="Low stock alerts" value={stats.low || 0} icon={ShieldCheck} accent="lavender" />
      </div>
      <Panel title="Quick actions">
        <div className="grid sm:grid-cols-2 gap-2">
          <Link to="/app/apparel" className="block p-3 rounded-xl bg-terracotta-soft hover:bg-terracotta-soft/80">
            <div className="text-terracotta font-semibold text-sm">Apparel ledger</div>
            <div className="text-xs text-ink-soft mt-1">Size-wise uniform stock + issue log.</div>
          </Link>
          <Link to="/app/vendors" className="block p-3 rounded-xl bg-sage-soft hover:bg-sage-soft/80">
            <div className="text-sage-deep font-semibold text-sm">Vendor orders</div>
            <div className="text-xs text-ink-soft mt-1">Place restock orders.</div>
          </Link>
          <Link to="/app/documents" className="block p-3 rounded-xl bg-mustard-soft hover:bg-mustard-soft/80">
            <div className="text-mustard-hover font-semibold text-sm">My documents</div>
            <div className="text-xs text-ink-soft mt-1">ID + qualifications.</div>
          </Link>
        </div>
      </Panel>
    </div>
  );
}


// ───────────────────────── Support Staff ─────────────────────────


export function SupportStaffDashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ tickets: 0, escalations: 0 });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/support-tickets/stats").catch(() => ({ data: { by_status: {}, total: 0 } }));
        setCounts({
          tickets: (r.data.by_status?.open || 0) + (r.data.by_status?.in_progress || 0),
          escalations: r.data.by_status?.escalated || 0,
        });
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div data-testid="support-staff-dashboard">
      <PageHeader
        eyebrow="Support"
        title={`Hi, ${user?.name?.split(" ")[0] || "Helper"} 🛟`}
        subtitle="Help parents, teachers and admins resolve day-to-day questions."
      />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard label="Active tickets" value={counts.tickets} icon={ChatCircleText} accent="terracotta" />
        <StatCard label="Escalations" value={counts.escalations} icon={ShieldCheck} accent="mustard" />
        <StatCard label="Customer happiness" value="— soon" icon={Heart} accent="sage" />
      </div>
      <Panel title="Quick actions">
        <div className="grid sm:grid-cols-2 gap-2">
          <Link to="/app/tech-support" className="block p-3 rounded-xl bg-terracotta-soft hover:bg-terracotta-soft/80">
            <div className="text-terracotta font-semibold text-sm">Open tickets</div>
            <div className="text-xs text-ink-soft mt-1">View the active ticket queue.</div>
          </Link>
          <Link to="/app/communications" className="block p-3 rounded-xl bg-sage-soft hover:bg-sage-soft/80">
            <div className="text-sage-deep font-semibold text-sm">Communications</div>
            <div className="text-xs text-ink-soft mt-1">Send updates to parents.</div>
          </Link>
          <Link to="/app/documents" className="block p-3 rounded-xl bg-mustard-soft hover:bg-mustard-soft/80">
            <div className="text-mustard-hover font-semibold text-sm">My documents</div>
            <div className="text-xs text-ink-soft mt-1">Upload ID + qualifications.</div>
          </Link>
        </div>
      </Panel>
    </div>
  );
}


// ───────────────────────── Vendor ─────────────────────────


export function VendorDashboard() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, o] = await Promise.all([
          api.get("/documents/stats/summary").catch(() => ({ data: null })),
          api.get("/vendors/orders").catch(() => ({ data: [] })),
        ]);
        setDocs(d.data);
        setOrders(o.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div data-testid="vendor-dashboard">
      <PageHeader
        eyebrow="Vendor Partner"
        title={`Welcome, ${user?.name || "Partner"}`}
        subtitle="Manage your supplies, agreements and documents with MangoCubs."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Open orders" value={orders.length} icon={Storefront} accent="terracotta" />
        <StatCard label="Docs verified" value={docs?.by_status?.verified || 0} icon={ShieldCheck} accent="sage" />
        <StatCard label="Docs pending" value={docs?.by_status?.pending_review || 0} icon={Clock} accent="mustard" />
        <StatCard label="Expiring (30d)" value={docs?.expiring_in_30_days || 0} icon={Heart} accent="lavender" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Recent orders">
          {orders.length === 0 ? (
            <div className="py-10 text-center text-ink-soft text-sm">No orders yet.</div>
          ) : (
            <div className="space-y-2">
              {orders.slice(0, 10).map((o, i) => (
                <div key={o.order_id || i} className="flex items-center justify-between p-3 rounded-lg bg-paper border border-border">
                  <div>
                    <div className="text-sm font-semibold">{o.title || o.item_name || "Order"}</div>
                    <div className="text-xs text-ink-soft">{o.quantity} units · {o.amount || "—"}</div>
                  </div>
                  <Pill tone={o.status === "completed" ? "success" : "warning"}>{o.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="My documents">
          <p className="text-sm text-ink-soft mb-3">
            Upload your GST certificate, PAN, business registration and agreement.
            Admins will review and approve.
          </p>
          <Link to="/app/documents">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white">
              <ShieldCheck size={16} /> Manage my documents
            </Button>
          </Link>
        </Panel>
      </div>
    </div>
  );
}
